import { useContext, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";

import { ConnectForm } from "@/utils/formHelper";
import { FormValidationMethods } from "@/components/Form/types";
import { QREditorContext } from "@/contexts/QREditorContext";
import { QREditorContextProps } from "@/contexts/QREditorContext/types";
import { AnalyticsKey, TrackingInputsProps, TrackingKey } from "./types";
import { Tracking } from "@/types/qr";
import { InputAdapter } from "@/components/Input/adapter/InputAdapter";

export const analyticsId = {
  googleAnalytics: "ga",
  facebookPixel: "mp",
  GTM: "gtm",
} as const;

export function TrackingInputsLib(props: TrackingInputsProps) {
  const { inputs } = props;

  const { t } = useTranslation("qr-editor-stats");
  const methods: FormValidationMethods = useFormContext();
  const { qrData, setQrData } = useContext<QREditorContextProps<any>>(QREditorContext);

  const [trackingInputs, setTrackingInputs] = useState<Tracking>(
    qrData?.data?.tracking ?? { ga: undefined, mp: undefined, gtm: undefined }
  );

  const onChange = useCallback((id: AnalyticsKey, value: string) => {
    const changedId = analyticsId[id] as TrackingKey;
    if (!changedId) return;

    setTrackingInputs((prev) => {
      return { ...prev, [changedId]: value === "" ? undefined : value };
    });
  }, []);

  useEffect(() => {
    if (!qrData) return;
    const newData = { ...qrData.data, tracking: trackingInputs };

    (Object.keys(analyticsId) as AnalyticsKey[]).forEach((key) => {
      if (newData[key] !== undefined) delete newData[key];
    });

    setQrData({ ...qrData, data: newData });
  }, [trackingInputs]);

  return (
    <ConnectForm>
      <>
        {inputs?.map((input, idx) => {
          const { label, placeholder, id, data_qa, errorMessage } = input;
          const analyticsKey = id as AnalyticsKey;
          const trackedKey = analyticsId[analyticsKey] as TrackingKey;

          return (
            <div
              key={data_qa}
              className="u-spacing-4"
            >
              <InputAdapter
                id={idx.toString()}
                type="text"
                name={id}
                label={t(label)}
                placeholder={t(placeholder)}
                value={trackingInputs?.[trackedKey] ?? ""}
                dataQA={data_qa}
                error={methods?.formState?.errors[id]}
                errorMessage={(methods?.formState?.errors[id]?.message as string) ?? undefined}
                onChange={(e) => onChange(analyticsKey, e.target.value)}
                rules={
                  errorMessage?.[0]?.rule
                    ? {
                        pattern: {
                          value: new RegExp(errorMessage[0].rule),
                          message: t(errorMessage[0].message),
                        },
                      }
                    : undefined
                }
              />
            </div>
          );
        })}
      </>
    </ConnectForm>
  );
}
